import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { FaFileExcel } from 'react-icons/fa';
import styles from './EmailMappings.module.css';
import Loader from '../Loader';

const API = process.env.REACT_APP_API_BASE_URL;
const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

/* ─── Modal wrapper ─── */
const Modal = ({ title, onClose, children }) => (
    <div className={styles.modalBackdrop}>
        <div className={styles.modalContainer}>
            <div className={styles.modalHeader}>
                <h3>{title}</h3>
                <button className={styles.closeBtn} onClick={onClose}>&times;</button>
            </div>
            <div className={styles.modalBody}>{children}</div>
        </div>
    </div>
);

const EmailMappings = () => {
    const [mappings, setMappings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Filters & sorting
    const [searchQuery, setSearchQuery] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'regionCode', direction: 'ascending' });
    const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'inactive'

    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Form fields
    const [formRegionCode, setFormRegionCode] = useState('');
    const [formRegionName, setFormRegionName] = useState('');
    const [formEmail, setFormEmail] = useState('');
    const [formName, setFormName] = useState('');
    const [formIsActive, setFormIsActive] = useState(true);

    // Edit/delete targets
    const [editTarget, setEditTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    // Lookup data for auto-populate
    const [regions, setRegions] = useState([]); // from /api/mappings/regions
    const [allUsers, setAllUsers] = useState([]); // from /api/users/users-with-regions

    /* ─── Fetch all mappings ─── */
    const fetchMappings = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API}/api/email-mappings`);
            setMappings(Array.isArray(response.data) ? response.data : []);
        } catch (err) {
            console.error('Error fetching email mappings:', err);
            toast.error('Failed to fetch email mappings');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchMappings(); }, []);

    // Fetch regions for the dropdown
    useEffect(() => {
        const fetchRegions = async () => {
            try {
                const res = await axios.get(`${API}/api/mappings/regions`, authHeaders());
                setRegions(Array.isArray(res.data) ? res.data : []);
            } catch (err) {
                console.error('Error fetching regions:', err);
            }
        };
        fetchRegions();
    }, []);

    // Fetch users for the sales person dropdown
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const res = await axios.get(`${API}/api/users/users-with-regions`, authHeaders());
                setAllUsers(Array.isArray(res.data) ? res.data : []);
            } catch (err) {
                console.error('Error fetching users:', err);
            }
        };
        fetchUsers();
    }, []);

    // Users filtered by selected region
    const usersForSelectedRegion = useMemo(() => {
        if (!formRegionName) return allUsers;
        return allUsers.filter(u =>
            u.RegionalCodes && u.RegionalCodes.some(rc => rc.regionName === formRegionName)
        );
    }, [allUsers, formRegionName]);

    /* ─── Filtered & sorted data ─── */
    const filteredMappings = useMemo(() => {
        let items = mappings;

        // Status filter
        if (statusFilter === 'active') {
            items = items.filter(m => m.isActive);
        } else if (statusFilter === 'inactive') {
            items = items.filter(m => !m.isActive);
        }

        // Search filter
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            items = items.filter(m =>
                m.regionName?.toLowerCase().includes(q) ||
                m.salesPersonEmail?.toLowerCase().includes(q) ||
                m.salesPersonName?.toLowerCase().includes(q) ||
                String(m.regionCode).includes(q)
            );
        }

        // Sort
        items = [...items].sort((a, b) => {
            const valA = a[sortConfig.key];
            const valB = b[sortConfig.key];
            if (valA == null) return 1;
            if (valB == null) return -1;
            if (typeof valA === 'string') {
                const cmp = valA.localeCompare(valB);
                return sortConfig.direction === 'ascending' ? cmp : -cmp;
            }
            return sortConfig.direction === 'ascending' ? valA - valB : valB - valA;
        });

        return items;
    }, [mappings, searchQuery, sortConfig, statusFilter]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return '';
        return sortConfig.direction === 'ascending' ? ' ▲' : ' ▼';
    };

    /* ─── CRUD handlers ─── */
    const resetForm = () => {
        setFormRegionCode('');
        setFormRegionName('');
        setFormEmail('');
        setFormName('');
        setFormIsActive(true);
        setEditTarget(null);
    };

    const getApiErrorMessage = (err, fallback) => {
        return err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;
    };

    // When region is selected from dropdown, auto-fill code and name
    const handleRegionSelect = (regionName) => {
        const region = regions.find(r => r.regionName === regionName);
        setFormRegionName(regionName);
        setFormRegionCode(region ? region.regionCode : '');
        // Reset sales person fields since region changed
        setFormName('');
        setFormEmail('');
    };

    // When user is selected from dropdown, auto-fill name and email
    const handleUserSelect = (username) => {
        const user = allUsers.find(u => u.Username === username);
        setFormName(username);
        setFormEmail(user?.Email || '');
    };

    const handleAdd = async () => {
        if (!formRegionCode || !formRegionName.trim() || !formEmail.trim()) {
            toast.error('Region code, region name, and email are required');
            return;
        }
        setSaving(true);
        try {
            await axios.post(`${API}/api/email-mappings`, {
                regionCode: Number(formRegionCode),
                regionName: formRegionName.trim(),
                salesPersonEmail: formEmail.trim(),
                salesPersonName: formName.trim(),
                isActive: formIsActive
            }, authHeaders());
            toast.success('Email mapping added successfully');
            setShowAddModal(false);
            resetForm();
            fetchMappings();
        } catch (err) {
            toast.error(getApiErrorMessage(err, 'Failed to add email mapping'));
        } finally {
            setSaving(false);
        }
    };

    const openEditModal = (mapping) => {
        setEditTarget(mapping);
        setFormRegionCode(mapping.regionCode);
        setFormRegionName(mapping.regionName);
        setFormEmail(mapping.salesPersonEmail);
        setFormName(mapping.salesPersonName || '');
        setFormIsActive(mapping.isActive);
        setShowEditModal(true);
    };

    const handleEdit = async () => {
        if (!formRegionCode || !formRegionName.trim() || !formEmail.trim()) {
            toast.error('Region code, region name, and email are required');
            return;
        }
        setSaving(true);
        try {
            await axios.put(`${API}/api/email-mappings/${editTarget.id}`, {
                regionCode: Number(formRegionCode),
                regionName: formRegionName.trim(),
                salesPersonEmail: formEmail.trim(),
                salesPersonName: formName.trim(),
                isActive: formIsActive
            }, authHeaders());
            toast.success('Email mapping updated successfully');
            setShowEditModal(false);
            resetForm();
            fetchMappings();
        } catch (err) {
            toast.error(getApiErrorMessage(err, 'Failed to update email mapping'));
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = async (mapping) => {
        setSaving(true);
        try {
            await axios.patch(`${API}/api/email-mappings/${mapping.id}/toggle`, {}, authHeaders());
            toast.success(`Mapping ${mapping.isActive ? 'deactivated' : 'activated'} successfully`);
            fetchMappings();
        } catch (err) {
            toast.error(getApiErrorMessage(err, 'Failed to toggle status'));
        } finally {
            setSaving(false);
        }
    };

    const openDeleteConfirm = (mapping) => {
        setDeleteTarget(mapping);
        setShowDeleteConfirm(true);
    };

    const handleDelete = async () => {
        setSaving(true);
        try {
            await axios.delete(`${API}/api/email-mappings/${deleteTarget.id}`, authHeaders());
            toast.success('Email mapping deleted successfully');
            setShowDeleteConfirm(false);
            setDeleteTarget(null);
            fetchMappings();
        } catch (err) {
            toast.error(getApiErrorMessage(err, 'Failed to delete email mapping'));
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className={styles.loading}>Loading email mappings...</div>;

    return (
        <div className={styles.container}>
            {saving && <Loader />}

            {/* ─── Header ─── */}
            <div className={styles.headerRow}>
                <h2 className={styles.title}>Regional Email Mappings</h2>
            </div>

            {/* ─── Toolbar: search (left) · actions (right) ─── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: '1 1 auto', flexWrap: 'wrap' }}>
                    <div className={styles.searchWrapper}>
                        <span className={styles.searchIcon}>🔍</span>
                        <input
                            type="text"
                            placeholder="Search by region, email, name..."
                            className={styles.searchInput}
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className={styles.statusFilterGroup}>
                        <button
                            className={`${styles.filterBtn} ${statusFilter === 'all' ? styles.filterBtnActive : ''}`}
                            onClick={() => setStatusFilter('all')}
                        >
                            All
                        </button>
                        <button
                            className={`${styles.filterBtn} ${statusFilter === 'active' ? styles.filterBtnActive : ''} ${styles.filterBtnGreen}`}
                            onClick={() => setStatusFilter('active')}
                        >
                            Active
                        </button>
                        <button
                            className={`${styles.filterBtn} ${statusFilter === 'inactive' ? styles.filterBtnActive : ''} ${styles.filterBtnRed}`}
                            onClick={() => setStatusFilter('inactive')}
                        >
                            Inactive
                        </button>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button className={styles.addBtn} onClick={() => { resetForm(); setShowAddModal(true); }}>
                        + Add Mapping
                    </button>
                    <button
                        className={styles.addBtn}
                        title="Export to Excel"
                        aria-label="Export to Excel"
                        style={{ background: '#16a34a', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '8px 12px' }}
                        onClick={() => {
                            const rows = filteredMappings.map(m => ({
                                'Region Code': m.regionCode,
                                'Region Name': m.regionName,
                                'Sales Person Name': m.salesPersonName || '',
                                'Sales Person Email': m.salesPersonEmail || '',
                                'Status': m.isActive ? 'Active' : 'Inactive',
                            }));
                            if (!rows.length) return toast.warn('No data to export.');
                            const ws = XLSX.utils.json_to_sheet(rows);
                            const wb = XLSX.utils.book_new();
                            XLSX.utils.book_append_sheet(wb, ws, 'Email Mappings');
                            XLSX.writeFile(wb, 'EmailMappings.xlsx');
                        }}
                    ><FaFileExcel size={20} /></button>
                </div>
            </div>

            {/* ─── Summary cards ─── */}
            <div className={styles.summaryCards}>
                <div className={styles.summaryCard}>
                    <span className={styles.summaryLabel}>Total</span>
                    <span className={styles.summaryValue}>{mappings.length}</span>
                </div>
                <div className={`${styles.summaryCard} ${styles.activeCard}`}>
                    <span className={styles.summaryLabel}>Active</span>
                    <span className={styles.summaryValue}>{mappings.filter(m => m.isActive).length}</span>
                </div>
                <div className={`${styles.summaryCard} ${styles.inactiveCard}`}>
                    <span className={styles.summaryLabel}>Inactive</span>
                    <span className={styles.summaryValue}>{mappings.filter(m => !m.isActive).length}</span>
                </div>
            </div>

            {/* ─── Table ─── */}
            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th className={styles.sortable} onClick={() => requestSort('regionCode')}>
                                RegionCodeID{getSortIndicator('regionCode')}
                            </th>
                            <th className={styles.sortable} onClick={() => requestSort('regionName')}>
                                Region Name{getSortIndicator('regionName')}
                            </th>
                            <th className={styles.sortable} onClick={() => requestSort('salesPersonName')}>
                                Sales Person{getSortIndicator('salesPersonName')}
                            </th>
                            <th className={styles.sortable} onClick={() => requestSort('salesPersonEmail')}>
                                Email{getSortIndicator('salesPersonEmail')}
                            </th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredMappings.length === 0 ? (
                            <tr><td colSpan="6" className={styles.noData}>No email mappings found</td></tr>
                        ) : (
                            filteredMappings.map(mapping => (
                                <tr key={mapping.id} className={!mapping.isActive ? styles.inactiveRow : ''}>
                                    <td>{mapping.regionCode}</td>
                                    <td>{mapping.regionName}</td>
                                    <td>{mapping.salesPersonName || '—'}</td>
                                    <td>{mapping.salesPersonEmail}</td>
                                    <td>
                                        <span
                                            className={`${styles.statusBadge} ${mapping.isActive ? styles.activeBadge : styles.inactiveBadge}`}
                                            onClick={() => handleToggle(mapping)}
                                            title="Click to toggle status"
                                        >
                                            {mapping.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className={styles.actionCell}>
                                        <button className={styles.editBtn} onClick={() => openEditModal(mapping)} title="Edit">✏️</button>
                                        <button className={styles.deleteIconBtn} onClick={() => openDeleteConfirm(mapping)} title="Delete">🗑️</button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* ─── Add Modal ─── */}
            {showAddModal && (
                <Modal title="Add Email Mapping" onClose={() => setShowAddModal(false)}>
                    <div className={styles.formGroup}>
                        <label>Region <span className={styles.required}>*</span></label>
                        <select value={formRegionName} onChange={e => handleRegionSelect(e.target.value)}>
                            <option value="">-- Select Region --</option>
                            {regions.map(r => (
                                <option key={r.regionCode} value={r.regionName}>{r.regionName}</option>
                            ))}
                        </select>
                    </div>
                    {formRegionName && (
                        <div className={styles.formGroup}>
                            <label>Region Code</label>
                            <input type="text" value={formRegionCode} disabled className={styles.readonlyInput} />
                        </div>
                    )}
                    <div className={styles.formGroup}>
                        <label>Sales Person <span className={styles.required}>*</span></label>
                        <select value={formName} onChange={e => handleUserSelect(e.target.value)}>
                            <option value="">-- Select Sales Person --</option>
                            {usersForSelectedRegion.map(u => (
                                <option key={u.UserID} value={u.Username}>{u.Username} ({u.Role})</option>
                            ))}
                        </select>
                    </div>
                    {formName && (
                        <div className={styles.formGroup}>
                            <label>Sales Person Email <span className={styles.required}>*</span></label>
                            <input type="email" value={formEmail} onChange={e => setFormEmail(e.target.value)} placeholder="Enter email if not auto-filled" />
                        </div>
                    )}
                    <div className={styles.formGroup}>
                        <label className={styles.checkboxLabel}>
                            <input type="checkbox" checked={formIsActive} onChange={e => setFormIsActive(e.target.checked)} />
                            Active
                        </label>
                    </div>
                    <div className={styles.modalFooter}>
                        <button className={styles.saveBtn} onClick={handleAdd} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                        <button className={styles.cancelBtn} onClick={() => setShowAddModal(false)}>Cancel</button>
                    </div>
                </Modal>
            )}

            {/* ─── Edit Modal ─── */}
            {showEditModal && (
                <Modal title="Edit Email Mapping" onClose={() => setShowEditModal(false)}>
                    <div className={styles.formGroup}>
                        <label>Region Code <span className={styles.required}>*</span></label>
                        <input type="number" value={formRegionCode} onChange={e => setFormRegionCode(e.target.value)} />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Region Name <span className={styles.required}>*</span></label>
                        <input type="text" value={formRegionName} onChange={e => setFormRegionName(e.target.value)} />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Sales Person Name</label>
                        <input type="text" value={formName} onChange={e => setFormName(e.target.value)} />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Sales Person Email <span className={styles.required}>*</span></label>
                        <input type="email" value={formEmail} onChange={e => setFormEmail(e.target.value)} />
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.checkboxLabel}>
                            <input type="checkbox" checked={formIsActive} onChange={e => setFormIsActive(e.target.checked)} />
                            Active
                        </label>
                    </div>
                    <div className={styles.modalFooter}>
                        <button className={styles.saveBtn} onClick={handleEdit} disabled={saving}>{saving ? 'Saving...' : 'Update'}</button>
                        <button className={styles.cancelBtn} onClick={() => setShowEditModal(false)}>Cancel</button>
                    </div>
                </Modal>
            )}

            {/* ─── Delete Confirmation ─── */}
            {showDeleteConfirm && deleteTarget && (
                <Modal title="Confirm Delete" onClose={() => setShowDeleteConfirm(false)}>
                    <p>Are you sure you want to delete the email mapping for <strong>"{deleteTarget.regionName}"</strong> ({deleteTarget.salesPersonEmail})?</p>
                    <div className={styles.modalFooter}>
                        <button className={styles.deleteBtn} onClick={handleDelete} disabled={saving}>{saving ? 'Deleting...' : 'Delete'}</button>
                        <button className={styles.cancelBtn} onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default EmailMappings;
